import * as React from 'react';

import { type HeadProps, type PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { ButtonLink } from 'stile-shared/src/components/2020/common/ButtonLink';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import { H3, P, Spacer, Text, Ul } from 'stile-shared/src/components/2020/common/Primitives';
import { Clamp } from 'stile-shared/src/components/2020/layout/Clamp';
import { Column } from 'stile-shared/src/components/2020/layout/Column';
import { Switcher } from 'stile-shared/src/components/2020/layout/Switcher';
import { CONTACT } from 'stile-shared/src/constants';
import { theme } from 'stile-shared/src/styles/2020/theme';

import { PageLayout } from 'templates/2020/PageLayout';
import { SEO } from 'templates/2020/SEO';

export function Head(props: HeadProps) {
  return (
    <SEO
      title="NEW: Scope and Sequence Builder"
      description="Curriculum planning made easy"
      slug={props.location.pathname}
    />
  );
}

function ScopeAndSequencePage(props: PageProps) {
  return (
    <PageLayout {...props}>
      <HeroBlock
        title="NEW: Scope and Sequence Builder"
        text="Curriculum planning made easy"
        variant="tight"
        image={
          <StaticImage
            loading="eager"
            aspectRatio={2.37}
            src="../../../../stile-shared/assets/images/scope-and-sequence-builder/cover.png"
            alt=""
          />
        }
      />

      <TextBlock>
        <P>
          As a customisable core curriculum, Stile provides everything you need to deliver a
          high-quality, curriculum-aligned science program. Our latest feature, the Scope and
          Sequence Builder,{' '}
          <strong>
            simplifies curriculum planning by empowering educators to craft a complete, customisable
            scope and sequence
          </strong>{' '}
          drawn directly from Stile’s engaging and up-to-date curriculum.
        </P>
      </TextBlock>

      <TextBlock>
        <Calendly
          title="Need a hand with curriculum planning?"
          text="Book in a free Curriculum Consultation where we can craft a customised Scope and Sequence using Stile's interactive tools. We can set up your School Library in Stile and provide recommendations to ensure your team gets the most out of Stile's resources."
          url="https://calendly.com/alexandra-russell/15min"
          image={
            <StaticImage
              src="../../../../stile-shared/assets/images/concierge/concierge_au_one-one.png"
              alt="An illustration of a Stile staff member helping a teacher learn how to use Stile"
            />
          }
        />
      </TextBlock>

      <TextBlock>
        <P>
          The Scope and Sequence Builder acts as a blueprint for tailoring your school’s science
          curriculum while ensuring seamless alignment within your state’s curriculum. Use as is, or
          customise to suit your needs.
        </P>

        <P>
          Designed to span a typical school year, you can effortlessly tailor the curriculum to
          match your school’s unique timetable and preferences. It serves as an excellent starting
          point for teachers, balancing the structured support required by graduate teachers and the
          flexibility experienced educators deserve.
        </P>
      </TextBlock>

      <TeaserBlock
        title="Free webinar: Build your Scope and Sequence with Stile"
        variant="dark"
        contentSide="right"
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/scope-and-sequence-builder/webinar.png"
            alt=""
          />
        }
      >
        <P>
          <strong>20th June | 4:00 PM - 4:30 PM AEST</strong>
        </P>
        <P>
          Join us for a fun, informative webinar all about our latest feature: Scope and Sequence
          Builder. Learn how to create a scope and sequence in Stile, using your curriculum as a
          starting point!
        </P>
        <ButtonLink url="https://www.bigmarker.com/stile-education1/Build-your-Scope-and-Sequence-with-Stile-5bca62893b6dadc133850a07?show_live_page=true">
          Reserve your spot
        </ButtonLink>
      </TeaserBlock>

      <TextBlock title="How to find the Scope and Sequence Builder">
        <P>
          Head to Stile and simply click into your School Library to find your school’s Scope and
          Sequence. Your Stile administrator (normally someone in a Head of Science position or
          similar) must first publish your school’s Scope and Sequence; more on this below!
        </P>
      </TextBlock>

      <StaticImage
        src="../../../../stile-shared/assets/images/scope-and-sequence-builder/where_to_find.png"
        alt=""
      />

      <Spacer />

      <TextBlock title="How to use the Scope and Sequence Builder" variant="beige">
        <H3>As a Stile administrator</H3>
        <P>
          Stile administrators get extra curating superpowers. You can customise your school’s Scope
          and Sequence by:
        </P>
        <Ul space={theme.space.xxs}>
          <li>
            Adding units from the School library, the broader Stile library, or any shared libraries
            by dragging them in from the sidebar
          </li>
          <li>
            Adjusting the duration of each unit by clicking and dragging on the right-hand side of
            the unit
          </li>
          <li>Reordering units by dragging and dropping them</li>
          <li>Adding, removing, and renaming Academic years and the terms within them</li>
        </Ul>
        <Spacer />
        <P>
          <strong>Editing and publishing your school’s Scope and Sequence</strong>
        </P>
        <StaticImage
          src="../../../../stile-shared/assets/images/scope-and-sequence-builder/get_started.png"
          alt=""
        />
        <Spacer />
        <P>
          The next time you log into Stile, you’ll be encouraged to start building your school’s
          Scope and Sequence.
        </P>
        <P>
          Simply click into your School Library and then the ‘Get Started’ button to begin building
          it. Drag in units from your pre-selected curriculum and modify as needed using existing
          units from the Stile Library or any shared libraries.
        </P>

        <Spacer />

        <H3>As a teacher</H3>
        <P>
          Once your school’s Stile administrator (often someone in a Head of Science position or
          similar) opts into this new feature and publishes one, you can view your school’s Scope
          and Sequence. This Scope and Sequence forms a handy reference point to plan our your
          science program in Stile!
        </P>
        <P>
          The “Scope and Sequence” tab lets you view your school’s custom Scope and Sequence,
          including unit order and duration for each year level.
        </P>
        <P>
          In the “Supplementary Content” tab, you’ll find all other available units not part of your
          Scope and Sequence.
        </P>
      </TextBlock>

      <TextBlock>
        <P>
          We hope this new feature saves you that little bit of planning time to free you up for
          what matters most: teaching! If you have any questions, don’t hesitate to get in touch
          with us at <Link url={CONTACT.email.help.url}>{CONTACT.email.help.text}</Link>.
        </P>
      </TextBlock>

      <CtaBlock />
    </PageLayout>
  );
}

export default ScopeAndSequencePage;

type CalendlyProps = {
  title: string;
  text: string;
  image: React.ReactNode;
  url: string;
};

function Calendly({ title, text, image, url }: CalendlyProps) {
  return (
    <Switcher threshold="450px" space={theme.space.sToM} align="center">
      <Clamp max="180px">{image}</Clamp>

      <Clamp max="500px">
        <Column alignSelf="center">
          <Column space={theme.space.xxxs} style={{ textAlign: 'left' }}>
            <Text as="h3" size="m" semiBold>
              {title}
            </Text>
            <Text size="s">{text}</Text>
          </Column>

          <ButtonLink url={url} openInNewTab>
            Book now
          </ButtonLink>
        </Column>
      </Clamp>
    </Switcher>
  );
}
